import styled from '@emotion/styled/macro';
import Divider from '@mui/material/Divider';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React, {
  Dispatch, SetStateAction, useEffect, useMemo, useState,
} from 'react';
import { MdOutlineClose } from 'react-icons/md';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { RoleType } from 'bluetail-globals';
import { SimpleSearch } from '../../../common/components/SimpleSearch';
import useAddAccountUserMutation, { AddAccountUserParams } from '../../hooks/useAddAccountUser';
import { AddAccountUserModalStepOneModel } from './AddAccountUserModalStepOne';
import { useAccountAircraft } from '../../../aircraft/hooks/useAccountAircraft';

const ModalHeader = styled.div`
  font-size: 20px;
  padding: 12px;
`;

const ModalBody = styled.div`
  display: flex;
  gap: 9px;
  flex-direction: column;
`;

const ModalDivider = styled(Divider)`
`;

const ModalHeaderActionContainer = styled(Stack)`
  justify-content: space-between;
`;

const CloseButton = styled(Button)`
  font-size: 20px;
  color: black;
`;

const SubmitButton = styled(Button)`
  width: 120px;
`;

const DataHeader = styled(Typography)`
  font-size: 14px;
`;

const DataCell = styled(Typography)`
  font-size: 12px;
`;

const StyledDialogContent = styled(DialogContent)`
  padding-top: 8px;
  min-height: 70vh;
`;

const StyledDataGrid = styled(DataGrid)`
  min-height: 50vh;
`;

type AddUserModalStepThreeAircraftProps = {
    handleClose: () => void;
    goToPreviousStep: () => void;
    userInfo: AddAccountUserModalStepOneModel;
    companyId: number;
    refreshUserList: () => void;
};

type ViewProps = Omit<AddUserModalStepThreeAircraftProps, 'companyId' | 'refreshUserList'> & {
    handleSubmit: () => void;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    aircrafts: (Aircraft & { selected: boolean; })[];
    numberOfSelectedAircraft: number;
    showOnlySelectedAircraft: boolean;
    setShowOnlySelectedAircraft: Dispatch<SetStateAction<boolean>>;
    allAircraftSelected: boolean;
    setAllAircraftSelected: Dispatch<SetStateAction<boolean>>;
    selectedAircrafts: Set<number>;
    setSelectedAircrafts: Dispatch<SetStateAction<Set<number>>>
};

function View({
  handleClose, handleSubmit, goToPreviousStep, userInfo, aircrafts, search, setSearch, numberOfSelectedAircraft,
  showOnlySelectedAircraft, setShowOnlySelectedAircraft, allAircraftSelected, setAllAircraftSelected, selectedAircrafts, setSelectedAircrafts,
}: ViewProps): React.JSX.Element {
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <ModalHeaderActionContainer spacing={2} direction="row">
        <ModalHeader>
          Select Aircraft For User
          {' '}
          <em>
            {userInfo.firstName}
            {' '}
            {userInfo.lastName}
          </em>
        </ModalHeader>
        <CloseButton variant="text" onClick={handleClose}>
          <MdOutlineClose />
        </CloseButton>
      </ModalHeaderActionContainer>
      <ModalDivider />
      <StyledDialogContent>
        <ModalBody>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography>
              User will receive access to
              {' '}
              {numberOfSelectedAircraft}
              {' '}
              total aircraft
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showOnlySelectedAircraft}
                  onChange={(e) => setShowOnlySelectedAircraft(e.target.checked)}
                />
                            )}
              label="Only show selected aircraft"
            />
          </Stack>
          <SimpleSearch search={search} setSearch={setSearch} placeHolder="Search Aircraft..." />
          <StyledDataGrid
            columns={[
              {
                renderHeader: () => (
                  <Checkbox
                    checked={allAircraftSelected}
                    onChange={(e) => setAllAircraftSelected(e.target.checked)}
                    indeterminate={!allAircraftSelected && selectedAircrafts.size > 0}
                  />
                ),
                field: 'selected',
                renderCell: ({ row: { payment_id } }) => (
                  <Checkbox
                    checked={allAircraftSelected || selectedAircrafts.has(payment_id as number)}
                    onChange={(e) => setSelectedAircrafts((p) => {
                      if (e.target.checked) {
                        p.add(payment_id as number);
                      } else {
                        p.delete(payment_id as number);
                      }

                      return new Set([...p.values()]);
                    })}
                  />
                ),
                sortable: false,
              },
              {
                renderHeader: () => <DataHeader>Tail Number</DataHeader>,
                field: 'tailnumber',
                flex: 1,
                renderCell: ({ row }) => (
                  <DataCell>
                    {row.reg_n_number}
                  </DataCell>
                ),
              },
              {
                renderHeader: () => <DataHeader>Serial Number</DataHeader>,
                field: 'serial_number',
                flex: 1,
                renderCell: ({ row: { serial_number } }) => <DataCell>{serial_number}</DataCell>,
              },
              {
                renderHeader: () => <DataHeader>Make</DataHeader>,
                field: 'manufacturer',
                flex: 1,
                renderCell: ({ row: { manufacturer } }) => <DataCell>{manufacturer}</DataCell>,
              },
              {
                renderHeader: () => <DataHeader>Model</DataHeader>,
                field: 'model',
                flex: 1,
                renderCell: ({ row: { model } }) => <DataCell>{model}</DataCell>,
              },
            ]}
            rows={aircrafts}
            disableRowSelectionOnClick
            getRowId={({ payment_id }) => payment_id as number}
            hideFooter
          />
        </ModalBody>
      </StyledDialogContent>
      <DialogActions>
        <Stack spacing={2} direction="row" justifyContent="end" width="100%">
          <Button onClick={goToPreviousStep}>Back</Button>
          <SubmitButton variant="contained" color="primary" onClick={handleSubmit}>
            Finish
          </SubmitButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function Model(props: AddUserModalStepThreeAircraftProps): React.JSX.Element {
  const { companyId, refreshUserList } = props;
  const { aircraft: allAircraft } = useAccountAircraft({
    company_id: companyId,
    min: 0,
    max: 100_000_000,
    searchText: '',
  }) as { aircraft: Aircraft[] };
  const [search, setSearch] = useState('');
  const [showOnlySelectedAircraft, setShowOnlySelectedAircraft] = useState(false);
  const [allAircraftSelected, setAllAircraftSelected] = useState(false);
  const [selectedAircrafts, setSelectedAircrafts] = useState<Set<number>>(new Set());
  const addAccountUserMutation = useAddAccountUserMutation();

  const allAircrafts = useMemo<ViewProps['aircrafts']>(
    () => allAircraft
      .filter((x) => x.recurly_status === 1)
      .map((x) => ({
        ...x,
        selected: allAircraftSelected || selectedAircrafts.has(x.payment_id),
      })),
    [allAircraft, allAircraftSelected, selectedAircrafts],
  );

  const allAircraftWithSelected = useMemo(
    () => allAircrafts
      .filter((x) => !showOnlySelectedAircraft || x.selected),
    [allAircrafts, showOnlySelectedAircraft],
  );

  const filteredAircraft = useMemo(() => {
    const normalizedSearch = search.toLowerCase().split(' ');
    return allAircraftWithSelected.filter((x) => normalizedSearch.every((s) => x.reg_n_number.toLowerCase().includes(s)
        || x.model.toLowerCase().includes(s)
        || x.serial_number.toLowerCase().includes(s)
        || x.manufacturer.toLowerCase().includes(s)));
  }, [allAircraftWithSelected, search]);

  const numberOfSelectedAircraft = useMemo(() => allAircraftWithSelected.filter((x) => x.selected).length, [allAircraftWithSelected]);

  useEffect(() => {
    setSelectedAircrafts(new Set());
  }, [allAircraftSelected]);

  const handleSubmit = (): void => {
    const {
      handleClose,
      userInfo: {
        firstName, lastName, email, userType, role, loginNeverExpires, loginExpirationDate,
      },
    } = props;

    const aircraftToAssign = allAircrafts
      .filter((x) => x.selected)
      .map((x) => x.payment_id);

    if (aircraftToAssign.length < 1) {
      toast.error('You must select at least one aircraft');
      return;
    }

    const params: AddAccountUserParams = {
      aircraft_assignment_method: 'aircraft',
      aircrafts: aircraftToAssign,
      fleets: null,
      company_id: companyId,
      first_name: firstName,
      last_name: lastName,
      email,
      login_expiry: loginNeverExpires ? null : loginExpirationDate.toISOString(),
      marketing_user_type: userType,
      role_id: role as RoleType,
    };

    addAccountUserMutation.mutate(params, {
      onSuccess: () => {
        toast.success(`User '${firstName} ${lastName}' created`);
        refreshUserList();
        handleClose();
      },
      onError: () => {
        toast.error('Error creating user');
      },
    });
  };

  const viewProps: ViewProps = {
    ...props,
    handleSubmit,
    search,
    setSearch,
    aircrafts: filteredAircraft,
    numberOfSelectedAircraft,
    showOnlySelectedAircraft,
    setShowOnlySelectedAircraft,
    allAircraftSelected,
    setAllAircraftSelected,
    selectedAircrafts,
    setSelectedAircrafts,
  };

  return <View {...viewProps} />;
}

export default Model;
export { View };
